import { groupUnavailableOriginal } from "@/utils/stockGrouping";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmt, BrandBadge } from "@/utils/format";
import StockBrandLogo from "@/components/StockBrandLogo";
import StockBarcode from "@/components/StockBarcode";
import VehicleCatalogFilter from "@/components/VehicleCatalogFilter";
import useDesktopLayout from "@/hooks/useDesktopLayout";
import { useCart } from "@/contexts/CartContext";
import { B2B } from "@/constants/testIds";
import { toast } from "sonner";
import { applyPastedStockSearch, isStockSearchReady, isStockSearchSubmitReady, normalizeStockSearch, rememberStockSearch } from "@/utils/stockSearch";
import {
  Search, ShoppingCart, ChevronLeft, ChevronRight, ArrowUp,
  XCircle, CheckCircle2, AlertCircle,
  ChevronDown, Package, X, BadgePercent,
} from "lucide-react";

const OEM_CHAR_LIMIT = 60;
const MOBILE_OEM_CHAR_LIMIT = 14;
const ORIGINAL_BRANDS = new Set(["VW ORİJİNAL", "PORSCHE ORİJİNAL"]);
const hasOemValue = (value) => Boolean(String(value || "").trim());
const hasNoOemMatch = (item) => !hasOemValue(item?.oem_no1) && !hasOemValue(item?.oem_no2);

function ResponsiveOemValue({ value }) {
  if (!value) return "—";
  return (
    <>
      <span className="sm:hidden">{value.slice(0, MOBILE_OEM_CHAR_LIMIT)}{value.length > MOBILE_OEM_CHAR_LIMIT ? "…" : ""}</span>
      <span className="hidden sm:inline">{value.slice(0, OEM_CHAR_LIMIT)}{value.length > OEM_CHAR_LIMIT ? "…" : ""}</span>
    </>
  );
}

// ── Stock indicator with tooltip ────────────────────────────────────────────
const STOCK_TOOLTIP = {
  out:  "Siparişle temin edebilirsiniz.",
  last: "Son kalan 1 adet",
  in:   "2+ Stok var",
};

function StockBadge({ qty }) {
  const n = qty ?? 0;

  const cfg =
    n <= 0
      ? { Icon: XCircle,      iconCls: "text-red-500",   labelCls: "text-red-500",   label: ["Stokta", "Yok"],  tip: STOCK_TOOLTIP.out  }
      : n === 1
      ? { Icon: AlertCircle,  iconCls: "text-amber-500", labelCls: "text-amber-600", label: ["Stokta", "Var"],  tip: STOCK_TOOLTIP.last }
      : { Icon: CheckCircle2, iconCls: "text-green-500", labelCls: "text-green-600", label: ["Stokta", "Var"],  tip: STOCK_TOOLTIP.in   };

  const { Icon, iconCls, labelCls, label, tip } = cfg;

  return (
    <div className="relative group flex flex-col items-center gap-0.5 w-16 shrink-0 cursor-default select-none">
      <Icon size={26} strokeWidth={2} className={iconCls} />
      <span className={`text-[10px] font-bold ${labelCls} uppercase tracking-wide text-center leading-tight`}>
        {label[0]}<br />{label[1]}
      </span>
      <div
        role="tooltip"
        className={`
          pointer-events-none absolute bottom-full mb-2.5
          left-1/2 -translate-x-1/2
          invisible opacity-0
          group-hover:visible group-hover:opacity-100
          transition-opacity duration-150 ease-out
          z-50 whitespace-nowrap
          bg-slate-900 text-white text-xs font-medium
          rounded-xl px-3 py-2 shadow-2xl
          border border-white/10
        `}
      >
        {tip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-slate-900" />
      </div>
    </div>
  );
}

// ── Dynamic brand dropdown ──────────────────────────────────────────────────
function BrandDropdown({ brands, selected, onSelect, loading }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = brands.filter((b) =>
    b.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
          selected
            ? "border-[#0D1B3E] bg-[#0D1B3E]/5 text-[#0D1B3E]"
            : "border-slate-300 bg-white text-slate-500 hover:border-slate-400"
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Package size={14} className={selected ? "text-[#0D1B3E]" : "text-slate-400"} />
          <span className="truncate">{selected || "Tüm Markalar"}</span>
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
          {/* Search bar */}
          <div className="p-2.5 border-b border-slate-100">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Marka ara..."
                className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#0D1B3E]/40 focus:ring-1 focus:ring-[#0D1B3E]/20 transition-colors"
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-60 overflow-y-auto overscroll-contain">
            {/* "Tüm Markalar" option */}
            <button
              onClick={() => { onSelect(""); setOpen(false); setQuery(""); }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors hover:bg-slate-50 ${
                !selected ? "font-semibold text-[#0D1B3E]" : "text-slate-600"
              }`}
            >
              <span>Tüm Markalar</span>
              {!selected && <CheckCircle2 size={14} className="text-[#0D1B3E]" />}
            </button>
            <div className="h-px bg-slate-100 mx-3" />

            {loading ? (
              <div className="px-4 py-4 text-center text-slate-400 text-sm">Yükleniyor...</div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-4 text-center text-slate-400 text-sm">Marka bulunamadı</div>
            ) : filtered.map((b) => (
              <button
                key={b}
                onClick={() => { onSelect(b); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors hover:bg-slate-50 ${
                  selected === b ? "font-semibold text-[#0D1B3E] bg-slate-50/80" : "text-slate-700"
                }`}
              >
                <span className="truncate">{b}</span>
                {selected === b && <CheckCircle2 size={14} className="text-[#0D1B3E] shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function B2BStok() {
  const [urlParams] = useSearchParams();
  const [stoklar, setStoklar]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [search, setSearch]         = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");   // "" = tümü
  const [inStock, setInStock]       = useState(false);
  const [brands, setBrands]         = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [info, setInfo]             = useState({});
  const [loading, setLoading]       = useState(false);
  const [oemModal, setOemModal]     = useState(null); // OEM NO2 tüm liste modal
  const [alternativeCount, setAlternativeCount] = useState(0);
  const [alternativesOnly, setAlternativesOnly] = useState(false);
  const [catalogFilter, setCatalogFilter] = useState(null);
  // Dynamic X-button position (follows typed text)
  const [xLeft, setXLeft]           = useState(0);
  const mirrorRef                   = useRef(null);
  const searchInputRef              = useRef(null);
  const loadMoreRef                 = useRef(null);
  const requestRef                  = useRef(null);
  const { addToCart, cart }         = useCart();
  const isDesktop = useDesktopLayout();

  const isExactMatch = search.trim().endsWith(",");
  const displayedStoklar = activeSearch.trim() ? groupUnavailableOriginal(stoklar, activeSearch) : stoklar;
  useEffect(() => {
    const query = normalizeStockSearch(urlParams.get("q") || "");
    setSearch(query);
    const submitted = urlParams.get("submit") === "1";
    setActiveSearch((submitted ? isStockSearchSubmitReady(query) : isStockSearchReady(query)) ? query : "");
    if (urlParams.get("focus") === "1") setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [urlParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setActiveSearch(isStockSearchReady(search) ? search : "");
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Per-item quantity state (defaults to the product's package quantity)
  const [quantities, setQuantities] = useState({});
  const getPack = (item) => Math.max(1, parseInt(item?.paket_adedi, 10) || 1);
  const getQty = (item) => quantities[item.id] ?? getPack(item);
  const setQty = (item, raw) => {
    const pack = getPack(item);
    const v = Math.max(pack, Math.ceil((parseInt(raw, 10) || pack) / pack) * pack);
    setQuantities((prev) => ({ ...prev, [item.id]: v }));
  };

  // Measure text pixel width so X button can follow the typed text
  useEffect(() => {
    if (!mirrorRef.current) return;
    const mirrorW  = mirrorRef.current.offsetWidth;
    const inputW   = searchInputRef.current?.offsetWidth ?? 380;
    const ICON_OFFSET = 40;   // pl-10 = 40px (Search icon area)
    const GAP         = 6;
    const X_BTN_W     = 24;
    // Clamp so X stays visible inside the input
    setXLeft(Math.min(ICON_OFFSET + mirrorW + GAP, inputW - X_BTN_W - 8));
  }, [search]);

  // Fetch distinct brands once on mount
  useEffect(() => {
    api.get("/stoklar/brands")
      .then((r) => setBrands(r.data.brands || []))
      .catch(() => {})
      .finally(() => setBrandsLoading(false));
  }, []);

  const fetchData = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const groupingSearch = Boolean(activeSearch.trim());
      const params = { page, per_page: groupingSearch ? 10 : 20 };
      if (activeSearch) params.search  = activeSearch;
      else if (!brandFilter && !inStock) params.vitrin_only = true;
      if (brandFilter) params.marka    = brandFilter;
      if (inStock)     params.in_stock = true;
      if (alternativesOnly) params.alternatives_only = true;
      const r = await api.get("/stoklar/b2b", { params, signal: controller.signal });
      const originals = page === 1 ? (r.data.originals || []) : [];
      setStoklar((previous) => page === 1 || !groupingSearch
        ? [...originals, ...r.data.items]
        : [...previous, ...r.data.items]);
      setTotal(r.data.total);
      setPages(r.data.pages || 1);
      setInfo({ eur_kuru: r.data.eur_kuru, fiyat_listesi: r.data.fiyat_listesi, oran: r.data.oran });
      if (page === 1 && !alternativesOnly) setAlternativeCount(r.data.available_alternative_count || 0);
      if (page === 1 && activeSearch && ((r.data.total || 0) > 0 || (r.data.originals || []).length > 0)) {
        rememberStockSearch(activeSearch);
        api.post("/stoklar/recent-searches", { query: activeSearch }).catch(() => {});
      }
    } catch (e) {
      if (e.code === "ERR_CANCELED") return;
      toast.error(formatApiError(e));
    } finally {
      if (requestRef.current === controller) setLoading(false);
    }
  }, [page, activeSearch, brandFilter, inStock, alternativesOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => () => requestRef.current?.abort(), []);
  useEffect(() => { setStoklar([]); setPage(1); }, [activeSearch, brandFilter, inStock, alternativesOnly]);
  useEffect(() => { setAlternativesOnly(false); setAlternativeCount(0); }, [activeSearch, brandFilter, inStock]);
  useEffect(() => {
    if (!activeSearch || page >= pages || !loadMoreRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loading) setPage((value) => value + 1);
    }, { rootMargin: "240px" });
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [activeSearch, page, pages, loading]);

  const handleAddToCart = (s) => {
    const qty = getQty(s);
    const availableStock = Number(s._effectiveStock ?? s.stok_durumu ?? 0);
    const isBackorder = availableStock <= 0;
    if (isBackorder && !window.confirm("Bu ürün stokta bulunmuyor. Bakiyeye almak istiyor musunuz?")) return;
    addToCart({
      stok_id: s.id,
      stok_kodu: s.kod,
      stok_adi: s.ad,
      birim_fiyat: s.b2b_fiyat,
      toplam: s.b2b_fiyat,
      b2b_fiyat: s.b2b_fiyat,
      b2b_fiyat_aciklama: s.b2b_fiyat_aciklama,
      paket_adedi: getPack(s),
      stok_durumu: availableStock,
      bakiye_talebi: isBackorder,
    }, qty);
    toast.success(`${qty} adet ${s.kod} ${isBackorder ? "bakiyeli olarak " : ""}sepete eklendi`);
    setQty(s, getPack(s));
  };

  const isInCart = (id) => cart.some((c) => c.stok_id === id);

  return (
    <div className="min-h-screen bg-slate-50">
      {process.env.REACT_APP_LOCAL_PREVIEW === "true" && <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-900">TecDoc + Parts Hub test ortamı · Fiyat ve stoklar yüklenen Wolvox dosyasındandır. Liste fiyatları KDV hariçtir; satış fiyatları Parts Hub kurallarıyla hesaplanır. Siparişler yalnızca bu test ortamına kaydedilir.</div>}

      {/* ── Sayfa başlığı ────────────────────────────────────────────────────── */}
      <div className="px-5 pt-5 pb-2 bg-slate-50">
        <h1
          className="text-2xl font-bold text-[#0D1B3E] tracking-tight"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Stok Listesi
        </h1>
        {info.fiyat_listesi && (
          <p className="text-sm text-slate-500 mt-0.5">
            {info.fiyat_listesi} · İskonto: %{((info.oran || 0) * 100).toFixed(0)} · KDV: %20 · EUR: {fmt(info.eur_kuru, 4)}
          </p>
        )}
      </div>

      <VehicleCatalogFilter
        drawAttention
        value={catalogFilter}
        onApply={async (selection) => {
          try {
            const { data } = await api.post("/catalog/resolve", {
              type_nos: selection.typeNos || [], engine_code: selection.engineCode || null,
              category: selection.category, subcategory: selection.subcategory || null, detail: selection.detail || null,
            });
            if (!data.primary_oem) return toast.error("Bu araç ve alt grup için aranacak OEM kodu bulunamadı");
            setBrandFilter(""); setAlternativesOnly(false); setPage(1);
            setCatalogFilter({ ...selection, primaryOem: data.primary_oem, primaryOemVerified: data.verified_by_human, primaryOemVerificationRole: data.verification_role, primaryOemVerifiedBy: data.verified_by, primaryOemVerifiedAt: data.verified_at });
            setSearch(data.primary_oem); setActiveSearch(data.primary_oem);
          } catch (error) { toast.error(formatApiError(error)); }
        }}
        onClear={() => { setCatalogFilter(null); setSearch(""); setActiveSearch(""); setAlternativesOnly(false); }}
      />

      {/* ── Filtre çubuğu (STICKY) ───────────────────────────────────────────── */}
      <div className={`${catalogFilter ? "" : "sm:sticky sm:top-14"} z-20 bg-white/96 backdrop-blur-md border-b border-slate-200 shadow-sm px-5 py-3 space-y-2.5`}>

        {/* Arama — X ikonu yazıyla birlikte kayar */}
        <form className="relative" onSubmit={(e) => { e.preventDefault(); if (isStockSearchSubmitReady(search)) { setPage(1); setActiveSearch(normalizeStockSearch(search).trim()); } else toast.error("Enter ile arama için en az 3 karakter girin"); }}>
          {/* Gizli mirror span: input'taki metnin piksel genişliğini ölçmek için */}
          <span
            ref={mirrorRef}
            aria-hidden="true"
            className="absolute left-10 top-3 text-sm whitespace-pre opacity-0 pointer-events-none select-none"
            style={{ fontFamily: "inherit" }}
          >
            {search}
          </span>

          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(normalizeStockSearch(e.target.value))}
            onPaste={(e) => {
              e.preventDefault();
              const input = e.currentTarget;
              setSearch(applyPastedStockSearch(search, e.clipboardData.getData("text"), input.selectionStart, input.selectionEnd));
            }}
            placeholder="OEM No, Stok Kodu, Stok Adı veya Barkod Ara...  (sonda virgül = kesin eşleşme)"
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-colors ${
              isExactMatch
                ? "border-amber-400 bg-amber-50 focus:ring-amber-300/40"
                : "border-slate-300 bg-white focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E]"
            }`}
          />

          {/* X (Temizle) — metnin hemen sağında dinamik konumda */}
          {search && !isExactMatch && (
            <button
              type="button"
              onClick={() => setSearch("")}
              title="Temizle"
              className="absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-900 transition-colors"
              style={{ left: `${xLeft}px` }}
            >
              <X size={13} strokeWidth={2.5} />
            </button>
          )}

          {/* Kesin eşleşme badge (tıklanınca temizler) */}
          {isExactMatch && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-amber-600 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full hover:bg-amber-200 transition-colors"
            >
              Kesin ×
            </button>
          )}
          {search && !isStockSearchReady(search) && (
            <p className="pt-1 pl-1 text-[11px] text-slate-400">Otomatik arama 6 karakterde başlar; 3 karakterden sonra Enter ile arayabilirsiniz.</p>
          )}
        </form>

        {/* İkinci satır: Stokta Var + Marka Dropdown */}
        <div className="flex items-center gap-2">

          {/* Stokta Var toggle */}
          <button
            onClick={() => setInStock((v) => !v)}
            className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-150 ${
              inStock
                ? "bg-green-500 border-green-500 text-white shadow-sm"
                : "border-slate-300 bg-white text-slate-600 hover:border-green-400 hover:text-green-600"
            }`}
          >
            <CheckCircle2 size={15} strokeWidth={2.5} className={inStock ? "text-white" : "text-slate-400"} />
            Stokta Var
          </button>

          {/* Dinamik marka dropdown */}
          <BrandDropdown
            brands={brands}
            selected={brandFilter}
            onSelect={(b) => setBrandFilter(b)}
            loading={brandsLoading}
          />

        </div>
      </div>

      {/* ── Aktif filtreler özeti ────────────────────────────────────────────── */}
      {(inStock || brandFilter) && (
        <div className="px-5 pt-2.5 flex flex-wrap gap-2">
          {inStock && (
            <span className="flex items-center gap-1.5 text-xs font-semibold bg-green-100 text-green-700 border border-green-200 px-3 py-1.5 rounded-full">
              <CheckCircle2 size={12} /> Stokta Var
              <button onClick={() => setInStock(false)} className="ml-1 text-green-500 hover:text-green-700">✕</button>
            </span>
          )}
          {brandFilter && (
            <span className="flex items-center gap-1.5 text-xs font-semibold bg-[#0D1B3E]/10 text-[#0D1B3E] border border-[#0D1B3E]/20 px-3 py-1.5 rounded-full">
              <Package size={12} /> {brandFilter}
              <button onClick={() => setBrandFilter("")} className="ml-1 opacity-60 hover:opacity-100">✕</button>
            </span>
          )}
        </div>
      )}

      {activeSearch && alternativeCount > 0 && !alternativesOnly && (
        <div className="mx-5 mt-2 flex min-h-9 items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900">
          <span className="font-semibold">Stokta {alternativeCount} alternatif ürün var</span>
          <button type="button" onClick={() => setAlternativesOnly(true)} className="shrink-0 rounded-md bg-emerald-600 px-3 py-1 font-semibold text-white hover:bg-emerald-700">Göster</button>
        </div>
      )}
      {activeSearch && alternativesOnly && (
        <div className="mx-5 mt-2 flex min-h-9 items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-900">
          <span className="font-semibold">Stoklu alternatif ürünler gösteriliyor</span>
          <button type="button" onClick={() => setAlternativesOnly(false)} className="shrink-0 font-semibold underline">Tüm sonuçlara dön</button>
        </div>
      )}

      {/* ── Ürün listesi ─────────────────────────────────────────────────────── */}
      <div className="px-5 pt-2 pb-4 space-y-3" data-testid={B2B.stokTable}>
        {loading && stoklar.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : stoklar.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">Ürün bulunamadı</div>
        ) : displayedStoklar.map((s) => (
          <div key={s.id} className={`relative min-w-0 bg-white border border-slate-200 rounded-xl p-4 lg:pl-[124px] lg:pr-[212px] min-h-[92px] shadow-sm ${hasOemValue(s.oem_no2) ? "lg:min-h-[172px]" : "lg:min-h-[148px]"}`}>
            <div className="absolute left-4 top-4">
              <StockBrandLogo brand={s.marka} />
            </div>
            <div aria-hidden="true" className="absolute bottom-4 left-[108px] top-4 hidden border-l border-slate-200 lg:block" />
            {isDesktop && (
              <div className="absolute left-4 top-[68px]"><StockBarcode value={s.barkod} /></div>
            )}

            {/* Ürün kodu + marka — büyütülmüş, belirgin */}
            <div className="min-h-16 min-w-0 pl-[92px] mb-4 lg:min-h-0 lg:pl-0 lg:mb-0">
            <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
              <span
                className="min-w-0 break-all font-mono text-sm font-bold text-[#0D1B3E] tracking-wide"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {s.kod}
              </span>
              <BrandBadge brand={s.marka} />
              {Number(s.kampanya_iskonto || 0) > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                  <BadgePercent size={12}/> Kampanya %{fmt(Number(s.kampanya_iskonto), 2)}
                </span>
              )}
            </div>

            {/* Ana OEM — oem_kodu doluysa göster, boşsa tamamen gizle */}
            {s.oem_kodu && (
              <div className="text-xs font-mono font-semibold text-blue-600 leading-snug break-all mb-2 select-text">
                {s.oem_kodu}
              </div>
            )}

            <div className="font-semibold text-slate-900 text-base leading-snug break-words lg:hidden">{s.ad}</div>
            </div>
            {/* Ana satır: ürün bilgisi | stok ikonu | fiyat+buton */}
            <div className="grid min-w-0 grid-cols-[64px_minmax(0,1fr)] items-start gap-4 lg:flex lg:items-start lg:gap-3">

              {/* Sol: ürün bilgisi */}
              <div className="col-span-2 flex-1 min-w-0">
                {/* Stok adı */}
                <div
                  className="hidden max-w-full break-words font-semibold text-slate-900 text-base leading-snug [overflow-wrap:anywhere] lg:block"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {s.ad}
                </div>

                {/* Referans alanları — sıralı ve hizalı */}
                <div className="relative mt-2 space-y-1">
                  {/* 1. Değişen No */}
                  <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-2">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wide w-20 shrink-0 whitespace-nowrap">Değişen No:</span>
                    <span className={`text-xs font-mono min-w-0 break-words ${s.degisen_no ? "text-slate-700 font-medium" : "text-slate-300"}`}>
                      {s.degisen_no ? `${s.degisen_no.slice(0, OEM_CHAR_LIMIT)}${s.degisen_no.length > OEM_CHAR_LIMIT ? "…" : ""}` : "—"}
                    </span>
                  </div>
                  {hasNoOemMatch(s) && <div className="text-xs font-medium text-slate-400">Henüz OEM eşleşmesi bulunamadı.</div>}
                  {/* 2. OEM NO1 */}
                  {hasOemValue(s.oem_no1) && <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-2 sm:items-start">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wide w-20 shrink-0 whitespace-nowrap">OEM NO1:</span>
                    <div className="min-w-0 leading-4">
                      <span className="text-xs font-mono font-semibold leading-4 break-words select-text text-blue-600"><ResponsiveOemValue value={s.oem_no1} /></span>
                      {s.oem_no1?.length > MOBILE_OEM_CHAR_LIMIT && <button onClick={() => setOemModal(s.oem_no1)} className="sm:hidden ml-1 text-xs text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}
                      {s.oem_no1?.length > OEM_CHAR_LIMIT && <button onClick={() => setOemModal(s.oem_no1)} className="hidden sm:inline ml-1 text-xs text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}
                    </div>
                  </div>}
                  {/* 3. OEM NO2 — aynı font/stil, 50 karakter sınırı, modal */}
                  {hasOemValue(s.oem_no2) && <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-2 sm:items-start">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wide w-20 shrink-0 whitespace-nowrap">OEM NO2:</span>
                    <div className="min-w-0 leading-4 sm:overflow-hidden">
                      <span className="text-xs font-mono font-semibold text-blue-600 leading-4 break-words select-text sm:break-all"><ResponsiveOemValue value={s.oem_no2} /></span>
                      {s.oem_no2.length > MOBILE_OEM_CHAR_LIMIT && (
                        <button
                          onClick={() => setOemModal(s.oem_no2)}
                          className="sm:hidden ml-1 text-xs text-[#0D1B3E] underline font-medium hover:text-blue-700 transition-colors whitespace-nowrap"
                        >
                          ... (tümü için tıklayın)
                        </button>
                      )}
                      {s.oem_no2.length > OEM_CHAR_LIMIT && (
                        <button onClick={() => setOemModal(s.oem_no2)} className="hidden sm:inline ml-1 text-xs text-[#0D1B3E] underline font-medium hover:text-blue-700 transition-colors whitespace-normal">... (tümü için tıklayın)</button>
                      )}
                    </div>
                  </div>}
                </div>

              </div>

              {/* Orta: stok göstergesi — sabit w-16 */}
              <div className="flex w-16 shrink-0 items-center justify-start pt-1 lg:absolute lg:right-[176px] lg:top-4 lg:w-auto lg:flex-col">
                <StockBadge qty={s._effectiveStock ?? s.stok_durumu} />
              </div>

              {/* Sağ: fiyat + miktar girişi + sepet butonu */}
              <div className="min-w-0 w-full shrink-0 flex flex-col items-end lg:absolute lg:right-4 lg:top-4 lg:w-40">
                <div
                  className="text-xl font-bold text-[#0D1B3E] tabular-nums whitespace-nowrap mb-2"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {Number(s.kampanya_iskonto || 0) > 0 && Number(s.b2b_normal_fiyat) > 0 && (
                    <div className="text-xs font-medium text-slate-400 line-through text-right">
                      {fmtCurrency(s.b2b_normal_fiyat)}
                    </div>
                  )}
                  {fmtCurrency(s.b2b_fiyat)}
                </div>

                {/* Miktar + Ekle — yan yana */}
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={getPack(s)}
                    step={getPack(s)}
                    value={getQty(s)}
                    onChange={(e) => setQty(s, e.target.value)}
                    onClick={(e) => e.target.select()}
                    className={`w-12 h-9 text-center text-sm font-semibold border border-slate-300 rounded-xl focus:outline-none focus:border-[#0D1B3E] focus:ring-1 focus:ring-[#0D1B3E]/20 tabular-nums bg-white ${(s.stok_durumu ?? 0) <= 0 ? "opacity-50" : ""}`}
                  />
                  <button
                    data-testid={`${B2B.addToCart}-${s.id}`}
                    onClick={() => handleAddToCart(s)}
                    className={`h-9 flex items-center gap-1 px-3 rounded-xl text-sm font-medium transition-all active:scale-[0.97] ${
                      (s.stok_durumu ?? 0) <= 0
                        ? "bg-slate-400 text-white hover:bg-slate-500 opacity-60"
                        : isInCart(s.id)
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : "bg-[#0D1B3E] text-white hover:bg-[#172A58]"
                    }`}
                  >
                    <ShoppingCart size={13} />
                    {isInCart(s.id) ? "Sepette" : "Ekle"}
                  </button>
                </div>
                {getPack(s) > 1 && <div className="mt-1 text-[10px] font-medium text-amber-600">Paket: {getPack(s)} adet</div>}
                <div className="text-[10px] text-right text-slate-400 mt-2 leading-snug max-w-40">{s.b2b_fiyat_aciklama}</div>
              </div>
            </div>
            {s._alternatives?.length > 0 && (
              <details className="mt-4 border-t border-slate-100 pt-3 lg:mr-[-196px]">
                <summary className="cursor-pointer text-sm font-semibold text-[#0D1B3E]">Stokta olmayan {s._alternatives.length} {s.marka} ürünü göster · Farklı fiyatlar mevcut</summary>
                <div className="mt-3 space-y-3">
                  {s._alternatives.map((alt) => <div key={alt.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-4 items-start lg:grid-cols-[100px_minmax(0,1fr)] lg:gap-6">
                      <div className="shrink-0 lg:h-full lg:border-r lg:border-slate-200 lg:pr-4"><StockBrandLogo brand={alt.marka} />{isDesktop && <StockBarcode value={alt.barkod} />}</div>
                      <div className="flex min-w-0 flex-col lg:flex-row gap-4 lg:items-start">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2"><span className="font-mono font-bold">{alt.kod}</span><BrandBadge brand={alt.marka} /></div>
                        <div className="font-semibold text-slate-900 mt-1">{alt.ad}</div>
                        <div className="mt-2 text-xs text-slate-500">Değişen No: <span className="font-mono">{alt.degisen_no || "—"}</span></div>
                        {hasNoOemMatch(alt) && <div className="text-xs font-medium text-slate-400">Henüz OEM eşleşmesi bulunamadı.</div>}
                        {hasOemValue(alt.oem_no1) && <div className="text-xs text-slate-500 sm:grid sm:grid-cols-[72px_minmax(0,1fr)] sm:items-start sm:gap-2"><span className="mr-1 sm:mr-0">OEM NO1:</span><span className="font-mono text-blue-600 break-all"><ResponsiveOemValue value={alt.oem_no1} /></span></div>}
                        {hasOemValue(alt.oem_no2) && <div className="min-w-0 text-xs text-slate-500 sm:grid sm:grid-cols-[72px_minmax(0,1fr)] sm:items-start sm:gap-2"><span className="mr-1 sm:mr-0">OEM NO2:</span><span className="min-w-0 sm:overflow-hidden sm:break-all"><span className="font-mono text-blue-600 break-all"><ResponsiveOemValue value={alt.oem_no2} /></span>{alt.oem_no2?.length > MOBILE_OEM_CHAR_LIMIT && <button onClick={() => setOemModal(alt.oem_no2)} className="sm:hidden ml-1 text-[#0D1B3E] underline font-medium whitespace-nowrap">(tümü için tıklayın)</button>}{alt.oem_no2?.length > OEM_CHAR_LIMIT && <button onClick={() => setOemModal(alt.oem_no2)} className="hidden sm:inline ml-1 text-[#0D1B3E] underline font-medium whitespace-normal">(tümü için tıklayın)</button>}</span></div>}
                      </div>
                      <div className="flex flex-col items-center"><StockBadge qty={alt._effectiveStock ?? alt.stok_durumu} /><span className="text-[10px] text-slate-400">{(alt._effectiveStock ?? alt.stok_durumu ?? 0) >= 2 ? "2+" : fmt(alt._effectiveStock ?? alt.stok_durumu ?? 0, 0)} {alt.birim}</span></div>
                      <div className="w-40 shrink-0 flex flex-col items-end"><div className="text-xl font-bold text-[#0D1B3E] whitespace-nowrap">{fmtCurrency(alt.b2b_fiyat)}</div><div className="flex items-center gap-1.5 mt-2"><input type="number" min={getPack(alt)} step={getPack(alt)} value={getQty(alt)} onChange={(e) => setQty(alt, e.target.value)} className={`w-12 h-9 text-center text-sm border rounded-xl ${(alt._effectiveStock ?? alt.stok_durumu ?? 0) <= 0 ? "opacity-50" : ""}`} /><button onClick={() => handleAddToCart(alt)} className={`h-9 px-3 rounded-xl text-sm text-white ${(alt._effectiveStock ?? alt.stok_durumu ?? 0) <= 0 ? "bg-slate-400 opacity-60" : "bg-[#0D1B3E]"}`}>Ekle</button></div>{getPack(alt) > 1 && <div className="mt-1 text-[10px] font-medium text-amber-600">Paket: {getPack(alt)} adet</div>}</div>
                      </div>
                    </div>
                  </div>)}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>

      {/* ── Sayfalama ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-4">
        <span className="text-sm text-slate-500">{total} {alternativesOnly ? "stoklu alternatif" : "ürün"}</span>
        {activeSearch ? (
          <div ref={loadMoreRef} className="text-sm text-slate-400">
            {page < pages ? (loading ? "Sonraki 10 ürün yükleniyor…" : "Devamı kaydırdıkça yüklenecek") : "Tüm sonuçlar gösterildi"}
          </div>
        ) : <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="p-2.5 rounded-xl text-slate-400 hover:text-slate-700 disabled:opacity-40 border border-slate-200 bg-white"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-slate-600 px-2 tabular-nums">{page} / {pages}</span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="p-2.5 rounded-xl text-slate-400 hover:text-slate-700 disabled:opacity-40 border border-slate-200 bg-white"
          >
            <ChevronRight size={16} />
          </button>
        </div>}
      </div>
      {activeSearch && (
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Sayfanın başına dön" title="Yukarı dön"
          className="fixed right-5 bottom-20 z-30 rounded-full bg-[#0D1B3E] p-3 text-white shadow-lg hover:bg-[#172A58]">
          <ArrowUp size={18} />
        </button>
      )}

      {/* ── OEM NO2 tüm numaralar modal ───────────────────────────────────── */}
      {oemModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setOemModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[70vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
              <div>
                <h3 className="text-sm font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  OEM NO2 — Tüm Numaralar
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {oemModal.split(",").filter(Boolean).length} numara
                </p>
              </div>
              <button
                onClick={() => setOemModal(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X size={17} />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-1 overscroll-contain">
              {oemModal.split(",").map((c) => c.trim()).filter(Boolean).map((code, i) => (
                <div key={i} className="text-xs font-mono font-semibold text-blue-600 py-1.5 border-b border-slate-50 last:border-0 select-text">
                  {code}
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 shrink-0">
              <button
                onClick={() => setOemModal(null)}
                className="w-full py-2.5 rounded-xl bg-[#0D1B3E] text-white text-sm font-medium hover:bg-[#172A58] transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
